import { type PortalSettings, type FieldValues, type BoardColumnFieldValue } from '@gorilla/portal-shared/src/schemas';
import { BoardColumnFieldDescriptor, SectionDescriptor } from '@gorilla/portal-shared/src/types';
import { Box, Flex, Icon, Text, Tooltip } from '@vibe/core';
import { Image as ImageIcon, LearnMore as LearnMoreIcon, Textcolor as TextIcon } from '@vibe/icons';
import { useBoardSourceResolver } from '../../hooks/use-board-source-resolver';
import { ColumnPicker } from '../../shared/components/ColumnPicker';

const ICONS_MAP: any = {
  image: ImageIcon,
  text: TextIcon,
};

type BoardColumnFieldProps = {
  value: BoardColumnFieldValue;
  fieldDescriptor: BoardColumnFieldDescriptor;
  sectionDescriptor: SectionDescriptor;
  portalSettings: PortalSettings;
  sectionFieldValues: FieldValues;
  clientBoardSubitemsBoardId: number | null;
  onChange: (value: BoardColumnFieldValue | undefined) => void;
  screenMode: 'small' | 'medium' | 'large';
};

export function BoardColumnField({
  value,
  fieldDescriptor,
  portalSettings,
  onChange,
  sectionDescriptor,
  sectionFieldValues,
  clientBoardSubitemsBoardId,
  screenMode,
}: BoardColumnFieldProps) {
  const { boardId, board, isLoading } = useBoardSourceResolver({
    portalSettings,
    sectionDescriptor,
    clientBoardSubitemsBoardId,
    sectionFieldValues,
  });

  if (!boardId || isLoading || !board) {
    return null;
  }

  const filteredColumns = (board.columns || []).filter((column) => {
    if (!fieldDescriptor.supportedColumnTypes) {
      return true;
    }

    return fieldDescriptor.supportedColumnTypes.includes(column.type);
  });

  return (
    <Box
      className={`section__field section__field--boardcolumn section__field--nomargin ${fieldDescriptor.backgroundNeutral ? 'section__field--backgroundneutral' : ''} ${fieldDescriptor.halfWidth ? (screenMode === 'medium' || screenMode === 'small' ? '' : 'section__field--halfwidth') : ''}`}
      border={true}
      borderColor="layoutBorderColor"
      backgroundColor="greyBackgroundColor"
    >
      <Box padding="medium">
        <Flex
          gap={24}
          style={{
            height: '32px',
          }}
        >
          <Flex gap={8}>
            {fieldDescriptor.icon && ICONS_MAP[fieldDescriptor.icon] ? (
              <Icon icon={ICONS_MAP[fieldDescriptor.icon]} size={20} style={{ transform: 'translate3d(0,0px,0)' }} />
            ) : null}
            <Text type="text2" weight="bold" align="start">
              {fieldDescriptor.title}
            </Text>
            {fieldDescriptor.description ? (
              <div>
                <Tooltip
                  title="What's this?"
                  content={fieldDescriptor.description}
                  moveBy={{ main: 5, secondary: -15 }}
                  position="right"
                  showOnDialogEnter={false}
                >
                  <Icon icon={LearnMoreIcon} size={16} style={{ transform: 'translate3d(0,2px,0)' }} />
                </Tooltip>
              </div>
            ) : null}
          </Flex>
          <Box style={{ marginLeft: 'auto', width: '180px' }}>
            <ColumnPicker
              columns={filteredColumns}
              value={value?.columnId}
              clearable={true}
              isSmall={true}
              hideIcons={true}
              placeholder="Select a column"
              onChange={(newColumn) => {
                if (newColumn === undefined) {
                  onChange(undefined);
                } else {
                  onChange({ columnId: newColumn.id });
                }
              }}
            />
          </Box>
        </Flex>
      </Box>
    </Box>
  );
}
